import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { Divider } from "primereact/divider";
import { Dialog } from "primereact/dialog";
import { toast } from "react-toastify";

import FormGroup from "../../common/components/form/FormGroup";
import UrgencyDropdown from "../urgencies/UrgenciesDropdown";
import UsersDropdown from "../users/UsersDropdown";
import TicketStatusDropdown from "../ticketStatuses/TicketStatusDropdown";
import { PropsWithClassName } from "../../common/types/PropsWithClassName";
import {
  changeAgent,
  changeOwner,
  changeStatus,
  changeTeam,
  changeCategory,
  changeUrgency,
  changeLocation,
  changeTitle,
} from "../tickets/ticketsApi";
import { selectAuthUser } from "../auth/authSlice";
import Label from "../../common/components/form/Label";
import TeamsDropdown from "../teams/TeamsDropdown";
import { UserRoleEnum } from "../userRoles/UserRole";
import TicketTypeDropdown from "../ticketType/TicketTypeDropdown";
import { TicketType } from "../ticketType/TicketType";
import CategoriesDropdown from "../categories/CategoriesDropdown";
import LocationsDropdown from "../locations/LocationsDropdown";
import DepartmentsDropdown from "../departments/DepartmentsDropdown";
import { fetchTicketTimeTracking } from "../ticketTimeTracking/ticketTimeTrackingApi";
import { useAppSelector } from "../../common/hooks/useStore";
import { selectDepartmentById } from "../departments/departmentsSlice";
import { closedStatusesIds, TicketStatusEnum } from "../ticketStatuses/TicketStatus";
import UserTicketsCard from "./UserTicketsCard";
import { Category } from "../categories/Category";
import TicketClassifier from "./TicketClassifier";
import { fetchSystemSettingByKey } from "../systemSettings/systemSettingsApi";
import { GenericSettingsApiKey } from "../systemSettings/SystemSettings";
import { fetchTicketStatusesByDepartment } from "../ticketStatuses/ticketStatusesApi";
import { InputText } from "primereact/inputtext";

interface TicketActionsProps extends PropsWithClassName {
  // TODO Paul: review this
  selectedTickets: {
    ticketId: string;
    agentId?: string;
  }[];
  ticketStatus?: string;
  ticketSubject?: string;
  teamId?: string;
  agentId?: string;
  ownerId?: string;
  urgency?: string;
  ticketType?: TicketType;
  departmentId?: string;
  categoryId?: string;
  locationId?: string;
  disabled?: boolean;
  onActionFinished?: () => void;
  // TODO Paul: review this
  setIsLoading?: (isLoading: boolean) => void;
}

export default function TicketActions(props: TicketActionsProps) {
  const authUser = useSelector(selectAuthUser);
  const department = useAppSelector((state) => selectDepartmentById(state, props.departmentId));
  const ticketIds = props.selectedTickets.map((t) => t.ticketId);
  const [selectedTicketStatus, setSelectedTicketStatus] = useState(props.ticketStatus);
  const [selectedTeamId, setSelectedTeamId] = useState(props.teamId);
  const [selectedAgentId, setSelectedAgentId] = useState(
    props.agentId ?? (ticketIds.length === 1 ? (props.selectedTickets?.[0]?.agentId ?? undefined) : undefined)
  );
  const [selectedOwnerId, setSelectedOwnerId] = useState(props.ownerId);
  const [selectedUrgency, setSelectedUrgency] = useState(props.urgency);
  const [selectedTicketType, setSelectedTicketType] = useState(props.ticketType);
  const [selectedCategoryId, setSelectedCategoryId] = useState(props.categoryId);
  const [selectedLocationId, setSelectedLocationId] = useState(props.locationId);
  const [selectedTitle, setSelectedTitle] = useState(props.ticketSubject);
  const [isUserTicketsHistoryDialogVisible, setIsUserTicketsHistoryDialogVisible] = useState(false);
  const [isSearchCategoryDialogVisible, setIsSearchCategoryDialogVisible] = useState(false);
  const [isCategoryAiSearchEnabled, setIsCategoryAiSearchEnabled] = useState(false);

  useEffect(() => {
    const doFetchSystemSettingByKey = async () => {
      const categoryAiSearchEnabled = await fetchSystemSettingByKey(GenericSettingsApiKey.CategoryAiSearchEnabled);
      setIsCategoryAiSearchEnabled(JSON.parse(categoryAiSearchEnabled.value));
    };
    doFetchSystemSettingByKey();
  }, []);

  // TODO Paul: refactor this
  useEffect(() => {
    setSelectedTicketStatus(props.ticketStatus);
  }, [props.ticketStatus]);

  useEffect(() => {
    setSelectedTeamId(props.teamId);
  }, [props.teamId]);

  useEffect(() => {
    setSelectedAgentId(props.agentId);
  }, [props.agentId]);

  useEffect(() => {
    setSelectedLocationId(props.locationId);
  }, [props.locationId]);

  const ticketHasTimeTrackings = async () => {
    if (!department?.timeTrackingEnabled) {
      return true;
    }

    const timeTrackings = await fetchTicketTimeTracking(ticketIds[0]);
    return timeTrackings.length > 0;
  };

  const onTicketStatusChange = async (ticketStatusId?: string) => {
    if (ticketIds.length === 1 && !selectedAgentId && ticketStatusId !== TicketStatusEnum.Escalated) {
      toast.error("Selecteaza un agent inainte de a schimba statusul");
      return;
    }
    if (ticketIds.length > 1 && !props.selectedTickets.every((t) => !!t.agentId)) {
      toast.error("Unul sau mai multe tichete nu au un agent atribuit");
      return;
    }

    const doChangeStatus = async () => {
      props.setIsLoading?.(true);
      setSelectedTicketStatus(ticketStatusId);
      if (ticketStatusId != null) {
        await changeStatus(ticketIds, ticketStatusId);
      }
      props.onActionFinished?.();
    };

    openConfirmDialog("Schimbare status", "Esti sigur ca vrei sa schimbi statusul?", async () => {
      const hasTimeTrackings = await ticketHasTimeTrackings();
      const isClosedStatus = closedStatusesIds.includes(ticketStatusId as TicketStatusEnum);
      if (!hasTimeTrackings && isClosedStatus) {
        setTimeout(() => {
          openConfirmDialog(
            "Schimbare status",
            "Tichetul nu are activitati logate. Esti sigur ca vrei sa continui cu schimbarea statusului?",
            doChangeStatus
          );
        }, 500);
      } else {
        doChangeStatus();
      }
    });
  };

  const onOwnerIdChange = async (ownerId?: string) => {
    openConfirmDialog("Schimbare utilizator", "Esti sigur ca vrei sa schimbi utilizatorul?", async () => {
      props.setIsLoading?.(true);
      setSelectedOwnerId(ownerId);
      if (ownerId) {
        await changeOwner(ticketIds, ownerId);
      }
      props.onActionFinished?.();
    });
  };

  const onTeamIdChange = async (teamId?: string) => {
    openConfirmDialog("Schimbare grupa", "Esti sigur ca vrei sa schimbi grupa?", async () => {
      props.setIsLoading?.(true);
      setSelectedTeamId(teamId);
      await changeTeam(ticketIds, teamId);
      props.onActionFinished?.();

      if (!props.departmentId || selectedTicketStatus === TicketStatusEnum.Escalated) {
        return;
      }
      const departmentTicketStatuses = await fetchTicketStatusesByDepartment(props.departmentId);
      const isEscalatedEnabled = departmentTicketStatuses.find((status) => status.id === TicketStatusEnum.Escalated)?.isEnabled;
      if (!isEscalatedEnabled || ticketIds.length > 1) {
        return;
      }
      setTimeout(() => {
        openConfirmDialog("Schimbare status", "Grupa a fost schimbata. Vrei sa schimbi statusul la 'Escalat'?", async () => {
          props.setIsLoading?.(true);
          setSelectedTicketStatus(TicketStatusEnum.Escalated);
          await changeStatus(ticketIds, TicketStatusEnum.Escalated);
          props.onActionFinished?.();
        });
      }, 500);
    });
  };

  const onAgentIdChange = async (agentId?: string) => {
    openConfirmDialog("Schimbare agent", "Esti sigur ca vrei sa schimbi agentul?", async () => {
      props.setIsLoading?.(true);
      setSelectedAgentId(agentId);
      await changeAgent(ticketIds, agentId);
      props.onActionFinished?.();
    });
  };

  const onUrgencyChange = async (urgency?: string) => {
    openConfirmDialog("Schimbare prioritate", "Esti sigur ca vrei sa schimbi prioritatea?", async () => {
      setSelectedUrgency(urgency);
      if (urgency != null) {
        await changeUrgency(ticketIds, urgency);
      }
      props.onActionFinished?.();
    });
  };

  const onCategoryChange = async (category?: Category, ticketType?: TicketType) => {
    const currentTicketType = ticketType ?? selectedTicketType;
    if (currentTicketType == null || category?.id == null) {
      return;
    }

    openConfirmDialog("Schimbare categorie", "Esti sigur ca vrei sa schimbi categoria / tipul tichetului?", async () => {
      setSelectedCategoryId(category?.id);
      await changeCategory(ticketIds, currentTicketType, category?.id);
      props.onActionFinished?.();
    });
  };

  const onLocationChange = async (locationId: string) => {
    openConfirmDialog("Schimbare locatie", "Esti sigur ca vrei sa schimbi locatia tichetului?", async () => {
      setSelectedLocationId(locationId);
      await changeLocation(ticketIds, locationId);
      props.onActionFinished?.();
    });
  };

  const onTitleChange = async () => {
    if (selectedTitle === props.ticketSubject) {
      return;
    }

    if (!selectedTitle || selectedTitle.trim() === "") {
      toast.error("Titlul este obligatoriu.");
      setSelectedTitle(props.ticketSubject);
      return;
    }

    openConfirmDialog(
      "Schimbare titlu",
      "Esti sigur ca vrei sa schimbi titlul tichetului?",
      async () => {
        await changeTitle(ticketIds, selectedTitle);
        props.onActionFinished?.();
      },
      () => {
        setSelectedTitle(props.ticketSubject);
      }
    );
  };

  const openConfirmDialog = async (header: string, text: string, onAccept?: () => {}, onReject?: () => void) => {
    if (props.disabled) {
      return;
    }

    confirmDialog({
      className: "w-almost-full md:w-4",
      icon: "pi pi-exclamation-triangle",
      header,
      message: <div className="text-xl">{text}</div>,
      acceptLabel: "Da",
      rejectLabel: "Nu",
      accept: () => onAccept?.(),
      reject: () => onReject?.(),
    });
  };

  return (
    <div className={`${props.className} ${props.disabled ? "opacity-50 pointer-events-none" : ""} flex flex-column`}>
      {ticketIds.length === 1 && (
        <FormGroup label="Titlu">
          <InputText value={selectedTitle} onChange={(e) => setSelectedTitle(e.target.value)} onBlur={onTitleChange} />
        </FormGroup>
      )}
      {props.departmentId != null && props.ticketType != null && (
        <FormGroup label="Departament">
          <DepartmentsDropdown value={props.departmentId} onChange={() => {}} disabled />
        </FormGroup>
      )}
      {props.departmentId != null && props.ticketType != null && (
        <>
          <FormGroup>
            <div className="flex align-items-center justify-content-between gap-2">
              <Label>Tip</Label>
              {isCategoryAiSearchEnabled && (
                <Button size="small" label="Cauta categorie" text onClick={() => setIsSearchCategoryDialogVisible(true)} />
              )}
            </div>
            <TicketTypeDropdown value={selectedTicketType} onChange={(ticketType) => setSelectedTicketType(ticketType)} />
          </FormGroup>
          {selectedTicketType != null && (
            <FormGroup label="Categorie">
              <CategoriesDropdown
                column
                ticketType={selectedTicketType}
                departmentId={props.departmentId}
                thirdLevelValue={selectedCategoryId}
                onChangeThirdLevel={onCategoryChange}
              />
            </FormGroup>
          )}
          <Divider className="mt-0" />
        </>
      )}
      {selectedLocationId && (
        <FormGroup label="Locatie">
          <LocationsDropdown value={selectedLocationId} onChange={onLocationChange} />
        </FormGroup>
      )}
      <FormGroup>
        <div className="flex align-items-center justify-content-between gap-2">
          <Label>Utilizator</Label>
          {selectedOwnerId && (
            <>
              <Button size="small" label="Vezi istoric" text onClick={() => setIsUserTicketsHistoryDialogVisible(true)} />
              <Dialog
                className="w-almost-full"
                visible={isUserTicketsHistoryDialogVisible}
                onHide={() => setIsUserTicketsHistoryDialogVisible(false)}
              >
                <UserTicketsCard className="shadow-none -m-3" userId={selectedOwnerId} />
              </Dialog>
            </>
          )}
        </div>
        <UsersDropdown value={selectedOwnerId} onChange={onOwnerIdChange}></UsersDropdown>
      </FormGroup>
      {props.departmentId != null && (
        <FormGroup label="Grupa">
          <TeamsDropdown departmentId={props.departmentId} value={selectedTeamId} onChange={onTeamIdChange}></TeamsDropdown>
        </FormGroup>
      )}
      <FormGroup>
        <div className="flex align-items-center justify-content-between gap-2">
          <Label>Agent</Label>
          <Button size="small" label="Atribuie mie" text onClick={() => onAgentIdChange(authUser.id)} />
        </div>
        <UsersDropdown
          placeholder="Selecteaza agent"
          teamIds={selectedTeamId ? [selectedTeamId] : undefined}
          role={UserRoleEnum.Agent}
          value={selectedAgentId}
          onChange={onAgentIdChange}
        ></UsersDropdown>
      </FormGroup>
      <Divider className="mt-0" />
      {props.departmentId != null && (
        <FormGroup label="Status">
          <TicketStatusDropdown
            departmentId={props.departmentId}
            value={selectedTicketStatus}
            onChange={onTicketStatusChange}
            ticketType={props.ticketType}
          ></TicketStatusDropdown>
        </FormGroup>
      )}
      {selectedTicketType === TicketType.Incident && (
        <FormGroup label="Prioritate">
          <UrgencyDropdown value={selectedUrgency} onChange={onUrgencyChange}></UrgencyDropdown>
        </FormGroup>
      )}
      <Dialog
        className="w-almost-full lg:w-8"
        header="Modifica categoria"
        visible={isSearchCategoryDialogVisible}
        onHide={() => setIsSearchCategoryDialogVisible(false)}
      >
        <TicketClassifier
          className="py-2"
          departmentId={props.departmentId}
          onCategorySelect={(category) => {
            setIsSearchCategoryDialogVisible(false);
            setSelectedTicketType(category.ticketType);
            onCategoryChange(category, category.ticketType);
          }}
        />
      </Dialog>
    </div>
  );
}
